/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2025 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  AGE_RANGE,
  BETWEEN,
  EQUAL,
  getSelectStyles,
  GREATER,
  GREATER_OR_EQUAL,
  LESS,
  LESS_OR_EQUAL,
  NOT_BETWEEN,
} from "../../../constant";
import {
  AgeRangeFieldProps,
  IntegerRangeConditionData,
} from "../../../types/rule";
import { OptionTypeBase } from "../../../types/common";

const AgeRange: FC<AgeRangeFieldProps> = ({
  disabled,
  id,
  parent,
  errors,
  condition,
  isHasScope = true,
  onValueChangeHandler,
  isDark,
}: AgeRangeFieldProps) => {
  const { t } = useTranslation("rule");

  const inputOptions = [EQUAL, GREATER, GREATER_OR_EQUAL, LESS, LESS_OR_EQUAL];
  const dispatchNewValue = (con: IntegerRangeConditionData) => {
    onValueChangeHandler(
      {
        type: AGE_RANGE,
        scope: isHasScope ? "conditions" : undefined,
        id,
        integerRangeProperty: {
          ...con,
          property: "ageRange",
        },
      },
      parent
    );
  };

  const updateValue = (event: React.FormEvent<HTMLInputElement>) => {
    dispatchNewValue({
      operator: condition?.operator,
      value: event.currentTarget.value,
    });
  };

  const updateFrom = (event: React.FormEvent<HTMLInputElement>) => {
    dispatchNewValue({
      operator: condition?.operator,
      to: condition?.to,
      from: event.currentTarget.value,
    });
  };

  const updateTo = (event: React.FormEvent<HTMLInputElement>) => {
    dispatchNewValue({
      operator: condition?.operator,
      from: condition?.from,
      to: event.currentTarget.value,
    });
  };

  const selectOperator = (update: any) => {
    dispatchNewValue({
      ...condition,
      operator: update?.value,
    });
  };

  const options = useMemo(() => {
    const result: Array<OptionTypeBase> = [];
    result.push(
      { label: t("equals"), value: EQUAL },
      { label: t("greater"), value: GREATER },
      { label: t("greater_or_equal"), value: GREATER_OR_EQUAL },
      { label: t("less"), value: LESS },
      { label: t("less_or_equal"), value: LESS_OR_EQUAL },
      { label: t("between"), value: BETWEEN },
      { label: t("not_between"), value: NOT_BETWEEN }
    );

    return result;
  }, [t]);

  const hasError = () => {
    return (
      errors.find((item) => item.id === id && item.type === AGE_RANGE) !==
      undefined
    );
  };

  return (
    <div className="row">
      <div className="mb-1 col col-md-3">
        <Select
          styles={getSelectStyles(isDark)}
          menuPlacement="auto"
          menuPosition="fixed"
          isDisabled={disabled}
          id={`${id}_operator`}
          name="operator"
          classNamePrefix="form-control-select"
          options={options}
          placeholder={t("select_operator")}
          onChange={selectOperator}
          value={options.find(
            (option) => option?.value === condition?.operator
          )}
        />
        {hasError() && !condition?.operator && (
          <label
            className="validation-invalid-label"
            htmlFor={`${id}_operator`}
          >
            {t("field_required")}
          </label>
        )}
      </div>
      {condition?.operator && inputOptions.includes(condition?.operator) && (
        <>
          <div className="col-sm-12 col-md-3">
            <input
              disabled={disabled}
              id={`${id}_value`}
              type="text"
              name="value"
              className="form-control mb-1"
              placeholder={t("value")}
              onChange={updateValue}
              value={condition?.value}
            />
            {hasError() && !condition?.value && (
              <label
                className="validation-invalid-label"
                htmlFor={`${id}_value`}
              >
                {t("field_required")}
              </label>
            )}
          </div>
        </>
      )}
      {condition?.operator &&
        [BETWEEN, NOT_BETWEEN].includes(condition?.operator) && (
          <>
            <div className="col-md-2 col-sm-12">
              <input
                disabled={disabled}
                type="text"
                name="from"
                id={`${id}_from`}
                className="form-control mb-1"
                value={condition?.from}
                placeholder={t("from")}
                onChange={updateFrom}
              />
              {hasError() && !condition?.from && condition?.from != "0" && (
                <label
                  className="validation-invalid-label"
                  htmlFor={`${id}_from`}
                >
                  {t("field_required")}
                </label>
              )}
            </div>
            <div className="col-md-2 col-sm-12">
              <input
                disabled={disabled}
                type="text"
                name="to"
                id={`${id}_to`}
                className="form-control mb-1"
                value={condition?.to}
                placeholder={t("to")}
                onChange={updateTo}
              />
              {hasError() && !condition?.to && condition?.to != "0" && (
                <label
                  className="validation-invalid-label"
                  htmlFor={`${id}_to`}
                >
                  {t("field_required")}
                </label>
              )}
            </div>
          </>
        )}
    </div>
  );
};

export default AgeRange;
